import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Check, Clock, Zap, ArrowRight, Calculator } from 'lucide-react';
import MatrixBackground from '@/MatrixBackground';
import MarketingHeader from '@/components/MarketingHeader';
import usePageSEO from '@/hooks/usePageSEO';

const plans = [
  {
    id: 'trial',
    name: 'Free Trial',
    price: '0',
    originalPrice: null,
    period: '',
    credits: 20,
    highlight: false,
    cta: 'Start Free Trial',
    features: [
      '20 credits to trial all tools',
      'Access to every AI legal tool',
      'No credit card required',
    ],
  },
  {
    id: 'basic',
    name: 'Basic',
    price: '14.99',
    originalPrice: '29.99',
    period: '/month',
    credits: 250,
    highlight: false,
    badge: '50% OFF',
    cta: 'Lock In This Price',
    features: [
      '250 monthly credits',
      'All AI legal tools',
      'Request new features',
      'Premium support',
      'Credits reset monthly',
    ],
  },
  {
    id: 'pro',
    name: 'Professional',
    price: '24.99',
    originalPrice: '49.99',
    period: '/month',
    credits: 600,
    highlight: true,
    badge: '50% OFF - Most Popular',
    cta: 'Lock In This Price',
    features: [
      '600 monthly credits',
      'All AI legal tools',
      'Request new features',
      'Priority support',
      'Credits reset monthly',
    ],
  },
  {
    id: 'small_law_firm',
    name: 'Small Law Firm',
    price: '99.99',
    originalPrice: '199.99',
    period: '/month',
    credits: 2000,
    highlight: false,
    badge: '50% OFF',
    cta: 'Lock In This Price',
    features: [
      '2,000 monthly credits',
      'All AI legal tools',
      'Request new features',
      'Dedicated support',
      'Credits reset monthly',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 'Custom',
    originalPrice: null,
    period: '',
    credits: null,
    highlight: false,
    cta: 'Contact Us',
    features: [
      'Unlimited credits',
      'All AI legal tools',
      'Custom feature requests',
      'Dedicated account manager',
      'Team management',
      'Priority SLA',
    ],
  },
];

const toolTimeSavings = [
  { tool: 'Contract Review', credits: 5, manualMins: 180, manualDisplay: '~3 hrs', savedPct: 90, savedDisplay: '~90%', description: 'Review and risk-flag a contract' },
  { tool: 'Due Diligence', credits: 5, manualMins: 240, manualDisplay: '4 hrs+', savedPct: 90, savedDisplay: '90%+', description: 'Analyse a due diligence document' },
  { tool: 'Legal Research', credits: 5, manualMins: 300, manualDisplay: '1 - 5 hrs', savedPct: 85, savedDisplay: '~85%', description: 'Research case law and precedents' },
  { tool: 'Document Generation', credits: 5, manualMins: 120, manualDisplay: '~2 hrs', savedPct: 90, savedDisplay: '~90%', description: 'Draft a legal document from scratch' },
  { tool: 'Deposition Prep', credits: 5, manualMins: 300, manualDisplay: '5 hrs+', savedPct: 85, savedDisplay: '85%+', description: 'Prepare deposition questions and witness analysis' },
  { tool: 'Document Comparison', credits: 5, manualMins: 180, manualDisplay: '~3 hrs', savedPct: 90, savedDisplay: '~90%', description: 'Compare two document versions clause-by-clause' },
  { tool: 'Legal Summarisation', credits: 5, manualMins: 120, manualDisplay: '1 - 3 hrs', savedPct: 90, savedDisplay: '~90%', description: 'Summarise a lengthy legal document' },
  { tool: 'Email Response', credits: 5, manualMins: 30, manualDisplay: '~30 mins', savedPct: 80, savedDisplay: '~80%', description: 'Draft a professional legal response' },
  { tool: 'Debt Recovery Letter', credits: 5, manualMins: 45, manualDisplay: '~45 mins', savedPct: 85, savedDisplay: '~85%', description: 'Generate a compliant recovery letter' },
  { tool: 'Ask Lawra', credits: 2, manualMins: 20, manualDisplay: '~20 mins', savedPct: 75, savedDisplay: '~75%', description: 'Quick legal query or research check' },
  { tool: 'More', credits: null, manualMins: null, manualDisplay: '—', savedPct: null, savedDisplay: '—', description: 'Additional AI-powered legal tools available', isPlaceholder: true },
];

const avgMinsPerCredit = toolTimeSavings.filter(t => !t.isPlaceholder).reduce((sum, t) => sum + (t.manualMins * t.savedPct / 100) / t.credits, 0) / toolTimeSavings.filter(t => !t.isPlaceholder).length;

const formatHours = (mins) => {
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  if (h === 0) return `${m} mins`;
  if (m === 0) return `${h} hrs`;
  return `${h} hrs ${m} mins`;
};

const Pricing = () => {
  const navigate = useNavigate();
  const [hourlyRate, setHourlyRate] = useState(250);

  usePageSEO({
    title: 'Pricing - 50% Off Early Access Sale | AI Legal Tools from £14.99/month | Lawra AI',
    description: 'Early Access Sale: 50% off all Lawra AI plans. Industry-leading AI legal tools for solicitors and law firms from just £14.99/month. Lock in your rate before prices return to normal.',
    path: '/pricing',
  });

  const planSavings = plans
    .filter((p) => p.credits)
    .map((p) => {
      const totalMins = p.credits * avgMinsPerCredit;
      const totalHours = totalMins / 60;
      const moneyValue = totalHours * hourlyRate;
      return { ...p, totalMins, totalHours, moneyValue };
    });

  const handleCTA = async (planId) => {
    try {
      const { auth } = await import('../firebase');
      if (auth.currentUser) {
        navigate('/dashboard');
      } else {
        navigate('/onboarding');
      }
    } catch {
      navigate('/onboarding');
    }
  };

  return (
    <div className="landing-page">
      <MatrixBackground />
      <MarketingHeader />

      <section className="sale-banner" data-testid="sale-banner">
        <div className="sale-banner-inner">
          <span className="sale-tag">EARLY ACCESS — LIMITED PLACES</span>
          <h2 className="sale-headline">Get Ahead of Other Law Firms — 50% Off</h2>
          <p className="sale-subtext">You're getting access to industry leading AI legal tools that will change how lawyers and law firms operate. Early access members lock in half-price rates permanently — once this window closes, full pricing applies.</p>
        </div>
      </section>

      {/* Hero */}
      <section className="pricing-hero" data-testid="pricing-hero">
        <div className="pricing-hero-inner">
          <p className="uc-hero-label">Early Access Pricing</p>
          <h1 className="uc-hero-title">50% Off Every Plan</h1>
          <p className="uc-hero-subtitle">
            Join the first wave of lawyers gaining an unfair advantage with AI. Lock in your early access rate now — before these tools become standard across the profession.
          </p>
        </div>
      </section>

      {/* Plan Cards */}
      <section className="pricing-plans" data-testid="pricing-plans">
        <div className="pricing-grid">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`pricing-card ${plan.highlight ? 'pricing-card--highlight' : ''}`}
              data-testid={`plan-${plan.id}`}
            >
              {plan.badge && <div className="pricing-badge">{plan.badge}</div>}
              <h3 className="pricing-card-name">{plan.name}</h3>
              <div className="pricing-card-price">
                {plan.originalPrice && (
                  <span className="pricing-original-price">£{plan.originalPrice}</span>
                )}
                {plan.price !== 'Custom' && <span className="pricing-currency">£</span>}
                <span className="pricing-amount">{plan.price}</span>
                {plan.period && <span className="pricing-period">{plan.period}</span>}
              </div>
              {plan.credits && (
                <p className="pricing-card-credits">
                  <Zap size={14} /> {plan.credits.toLocaleString()} credits / month
                </p>
              )}
              {!plan.credits && plan.id === 'enterprise' && (
                <p className="pricing-card-credits">
                  <Zap size={14} /> Unlimited credits
                </p>
              )}
              <ul className="pricing-features">
                {plan.features.map((f, i) => (
                  <li key={i}><Check size={16} className="pricing-check" /> {f}</li>
                ))}
              </ul>
              <Button
                className={plan.highlight ? 'pricing-cta pricing-cta--primary' : 'pricing-cta'}
                data-testid={`cta-${plan.id}`}
                onClick={() => plan.id === 'enterprise' ? window.location.href = 'mailto:sales@lawra-ai.com?subject=Enterprise%20Plan%20Enquiry%20-%20Lawra%20AI&body=Hi%20Lawra%20AI%20Team%2C%0A%0AI%20am%20interested%20in%20the%20Enterprise%20plan.%20Please%20find%20my%20details%20below%3A%0A%0AFull%20Name%3A%0ALaw%20Firm%3A%0ARole%2FPosition%3A%0ANumber%20of%20Users%3A%0APhone%20Number%3A%0A%0ASpecific%20requirements%20or%20questions%3A%0A%0AThank%20you' : handleCTA(plan.id)}
              >
                {plan.cta} {plan.id !== 'enterprise' && <ArrowRight size={16} />}
              </Button>
            </div>
          ))}
        </div>
      </section>

      {/* Time Savings Per Tool */}
      <section className="pricing-savings" data-testid="pricing-savings">
        <div className="pricing-savings-inner">
          <div className="pricing-savings-header">
            <Clock size={28} className="pricing-savings-icon" />
            <h2 className="pricing-savings-title">Estimated Time Saved Per Credit</h2>
            <p className="pricing-savings-subtitle">
              Each tool use replaces hours of manual legal work. Here's a rough guide to what each credit saves you.
            </p>
          </div>

          <div className="savings-table-wrapper">
            <table className="savings-table" data-testid="savings-table">
              <thead>
                <tr>
                  <th>Tool</th>
                  <th>What It Does</th>
                  <th>Credits</th>
                  <th>Approx. Manual Time</th>
                  <th>Est. Time Saved</th>
                </tr>
              </thead>
              <tbody>
                {toolTimeSavings.map((t, i) => (
                  <tr key={i} className={t.isPlaceholder ? 'savings-placeholder-row' : ''}>
                    <td className="savings-tool-name">{t.tool}</td>
                    <td className="savings-desc">{t.description}</td>
                    <td className="savings-credits">{t.credits ?? '—'}</td>
                    <td className="savings-manual">{t.manualDisplay}</td>
                    <td className="savings-per-credit">{t.savedDisplay}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="pricing-caveat">
            * Time estimates are approximate and may vary significantly depending on document complexity, user experience, and the specific task. Actual time saved could be more or less than the figures shown. These estimates are intended as a general guide only.
          </p>
        </div>
      </section>

      {/* ROI Calculator */}
      <section className="pricing-roi" data-testid="pricing-roi">
        <div className="pricing-roi-inner">
          <div className="pricing-roi-header">
            <Calculator size={28} className="pricing-savings-icon" />
            <h2 className="pricing-savings-title">What Your Credits Are Worth</h2>
            <p className="pricing-savings-subtitle">
              Estimated value each plan delivers monthly, based on average time saved across all tools.
            </p>
            <div className="roi-rate-input">
              <label htmlFor="hourly-rate">Your hourly rate (£)</label>
              <input
                id="hourly-rate"
                type="number"
                min="50"
                max="1000"
                step="25"
                value={hourlyRate}
                onChange={(e) => setHourlyRate(Number(e.target.value) || 250)}
                className="roi-input"
                data-testid="hourly-rate-input"
              />
            </div>
          </div>

          <div className="roi-cards">
            {planSavings.map((p) => (
              <div key={p.id} className={`roi-card ${p.highlight ? 'roi-card--highlight' : ''}`} data-testid={`roi-${p.id}`}>
                <h4 className="roi-card-name">{p.name}</h4>
                <p className="roi-card-cost">£{p.price}/mo <span className="roi-was-price">(was £{p.originalPrice})</span></p>
                <div className="roi-card-stats">
                  <div className="roi-stat">
                    <span className="roi-stat-value">~{formatHours(p.totalMins)}</span>
                    <span className="roi-stat-label">Est. time saved / month</span>
                  </div>
                  <div className="roi-stat">
                    <span className="roi-stat-value roi-stat-money">~£{Math.round(p.moneyValue).toLocaleString()}</span>
                    <span className="roi-stat-label">Est. value of time saved</span>
                  </div>
                  {parseFloat(p.price) > 0 && (
                    <div className="roi-stat">
                      <span className="roi-stat-value roi-stat-roi">~{Math.round(p.moneyValue / parseFloat(p.price))}x</span>
                      <span className="roi-stat-label">Potential ROI</span>
                    </div>
                  )}
                  {parseFloat(p.price) === 0 && (
                    <div className="roi-stat">
                      <span className="roi-stat-value roi-stat-roi">Free</span>
                      <span className="roi-stat-label">No cost to try</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <p className="pricing-caveat">
            * These figures are theoretical estimates for illustrative purposes only. Actual savings depend on individual usage patterns, document complexity, hourly rates, and user efficiency. Results will vary.
          </p>

          <div className="roi-calculator-link">
            <Button
              variant="ghost"
              className="use-cases-nav-link"
              onClick={() => navigate('/credit-calculator')}
              data-testid="full-calculator-link"
            >
              Open Full Credit Worth Calculator <ArrowRight size={16} />
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="pricing-faq" data-testid="pricing-faq">
        <div className="pricing-faq-inner">
          <h2 className="pricing-savings-title">Common Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h4>How do credits work?</h4>
              <p>Each AI tool use costs credits (5 for most tools, 2 for Ask Lawra). Credits reset at the start of each billing cycle with no rollover.</p>
            </div>
            <div className="faq-item">
              <h4>Can I change plan at any time?</h4>
              <p>Yes. Upgrade or downgrade from your Account Settings. Changes take effect on your next billing cycle.</p>
            </div>
            <div className="faq-item">
              <h4>Is my data secure?</h4>
              <p>All documents are processed securely and never used to train AI models. We use enterprise-grade encryption for data in transit and at rest.</p>
            </div>
            <div className="faq-item">
              <h4>Can I cancel my subscription?</h4>
              <p>Cancel anytime from your Account Settings or Stripe Customer Portal. You'll retain access until the end of your current billing period.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section" data-testid="pricing-cta-section">
        <div className="cta-content">
          <h2>Start With 20 Free Credits</h2>
          <p>No credit card required. Try every tool and see the time savings for yourself.</p>
          <Button
            className="try-now-btn"
            size="lg"
            data-testid="pricing-bottom-cta"
            onClick={() => handleCTA('trial')}
          >
            Start Your Free Trial
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
